/**
 * printUtils.js
 * Professional consignment print — matches customer invoice print style
 * Usage: import { printConsignment } from '../utils/printUtils';
 *        printConsignment(formData, 'LT');
 */

export const printConsignment = (data, type = 'LT') => {

  const fmt     = (n) => n ? parseFloat(n).toFixed(2) : '0.00';
  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const fmtDateTime = (d) => d
    ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  const val = (v) => v || '—';

  const typeLabel = type === 'LT' ? 'Land Transport' : type === 'AIR' ? 'Air Freight' : type;

  // Status label & color
  const statusMap = { 1: ['Pending', '#f59e0b'], 2: ['In Transit', '#3b82f6'], 3: ['Delivered', '#10b981'], 4: ['Returned', '#ef4444'] };
  const [statusLabel, statusColor] = statusMap[data.CourierStatusID] || ['Pending', '#f59e0b'];

  /* ── Route row ── */
  const origin      = data.OriginCity      || data.OriginCityName      || '—';
  const destination = data.DestinationCity || data.DestinationCityName || '—';

  /* ── Package rows ── */
  const packageRows = [
    ['No. of Packages',     val(data.NoOfPkgs)],
    ['Package Type',         val(data.PackageType)],
    ['Pieces',               val(data.Pieces)],
    ['Container Type',       val(data.ContainerType || data.ContainerTypeName)],
    ['Container No. 1',      val(data.ContainerNo)],
    ['Container No. 2',      val(data.ContainerNo2)],
  ].filter(([, v]) => v !== '—');

  /* ── Dimensions ── */
  const hasDimensions = data.Length || data.Width || data.Height;
  const dimensionRow  = hasDimensions
    ? `${val(data.Length)} × ${val(data.Width)} × ${val(data.Height)} cm`
    : '—';

  /* ── Payment & charges ── */
  const hasCharges = data.TransportationCharge || data.MaterialCost || data.CODAmount;

  /* ── Two-column info table helper ── */
  const infoRow = (label, value, mono = false) =>
    value && value !== '—' ? `
      <tr>
        <td style="padding:7px 12px;border-bottom:1px solid #f1f5f9;width:38%;font-size:12px;color:#6b7280;white-space:nowrap">${label}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f1f5f9;font-size:12px;font-weight:600;color:#111;${mono ? 'font-family:monospace' : ''}">${value}</td>
      </tr>` : '';

  /* ── Section header ── */
  const sectionHeader = (title, color = '#1e3a5f') => `
    <div style="background:${color};color:#fff;padding:8px 14px;border-radius:6px;font-size:11px;font-weight:700;letter-spacing:0.08em;margin-bottom:0;margin-top:20px">
      ${title}
    </div>`;

  const html = `<!DOCTYPE html><html><head>
    <title>Consignment — ${data.ConsignmentNo}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; font-size: 13px; color: #1a1a1a; background: #fff; }
      @page { margin: 12mm 14mm; }
      @media print { body { padding: 0; } .no-print { display: none !important; } }
      table { border-collapse: collapse; width: 100%; }
    </style>
  </head><body>

  <!-- ══ HEADER ══ -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:18px;border-bottom:3px solid #1e3a5f">
    <div>
      <div style="font-size:26px;font-weight:800;color:#1e3a5f;letter-spacing:-0.5px">CONSIGNMENT NOTE</div>
      <div style="font-size:13px;color:#6b7280;margin-top:4px">${typeLabel} · Road Freight</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:22px;font-weight:800;color:#1e3a5f">LTMS</div>
      <div style="font-size:11px;color:#6b7280;margin-top:2px">Logistics &amp; Transport Management</div>
      <div style="margin-top:8px;background:#1e3a5f;color:#fff;padding:6px 14px;border-radius:6px;font-family:monospace;font-size:14px;font-weight:700;letter-spacing:0.04em">
        ${val(data.ConsignmentNo)}
      </div>
    </div>
  </div>

  <!-- ══ META BAR ══ -->
  <div style="display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap">
    ${[
      ['Date', fmtDateTime(data.ConsignmentDate)],
      ['Despatch Date', fmtDate(data.DespatchDate)],
      ['Billing Type', val(data.BillingType)],
      ['Payment Mode', val(data.PaymentMode)],
      ['Load Type', val(data.Load)],
    ].filter(([,v]) => v !== '—').map(([label, value]) => `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;min-width:120px;flex:1">
        <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.07em;margin-bottom:3px">${label.toUpperCase()}</div>
        <div style="font-size:12px;font-weight:700;color:#111">${value}</div>
      </div>`).join('')}
    <div style="background:${statusColor}18;border:1.5px solid ${statusColor};border-radius:8px;padding:10px 14px;min-width:120px;flex:1">
      <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.07em;margin-bottom:3px">STATUS</div>
      <div style="font-size:12px;font-weight:800;color:${statusColor}">${statusLabel}</div>
    </div>
  </div>

  <!-- ══ ROUTE ══ -->
  <div style="background:linear-gradient(135deg,#1e3a5f,#2563eb);border-radius:10px;padding:16px 20px;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between">
    <div style="text-align:center;flex:1">
      <div style="font-size:10px;font-weight:700;color:rgba(255,255,255,0.7);letter-spacing:0.08em;margin-bottom:4px">ORIGIN</div>
      <div style="font-size:18px;font-weight:800;color:#fff">${origin}</div>
    </div>
    <div style="font-size:28px;color:rgba(255,255,255,0.5);padding:0 16px">→</div>
    <div style="text-align:center;flex:1">
      <div style="font-size:10px;font-weight:700;color:rgba(255,255,255,0.7);letter-spacing:0.08em;margin-bottom:4px">DESTINATION</div>
      <div style="font-size:18px;font-weight:800;color:#fff">${destination}</div>
    </div>
  </div>

  <!-- ══ SHIPPER + CONSIGNEE ══ -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:20px">

    <!-- Shipper -->
    <div style="border:1.5px solid #bfdbfe;border-radius:10px;overflow:hidden">
      <div style="background:#1e3a5f;color:#fff;padding:9px 14px;font-size:11px;font-weight:700;letter-spacing:0.07em">
        CONSIGNOR / SHIPPER
      </div>
      <div style="padding:12px 14px;background:#f0f9ff">
        <div style="font-size:15px;font-weight:700;margin-bottom:6px">${val(data.ShipperName)}</div>
        ${data.ShipperCode ? `<div style="font-size:11px;color:#6b7280;font-family:monospace;margin-bottom:4px">${data.ShipperCode}</div>` : ''}
        ${data.ShipperContactPerson ? `<div style="font-size:12px;color:#374151;margin-bottom:2px">Contact: ${data.ShipperContactPerson}</div>` : ''}
        ${data.ShipperPhone ? `<div style="font-size:12px;color:#374151;margin-bottom:2px">📞 ${data.ShipperPhone}</div>` : ''}
        ${data.ShipperWhatsApp ? `<div style="font-size:12px;color:#374151">WhatsApp: ${data.ShipperWhatsApp}</div>` : ''}
      </div>
    </div>

    <!-- Consignee -->
    <div style="border:1.5px solid #bbf7d0;border-radius:10px;overflow:hidden">
      <div style="background:#065f46;color:#fff;padding:9px 14px;font-size:11px;font-weight:700;letter-spacing:0.07em">
        CONSIGNEE / RECEIVER
      </div>
      <div style="padding:12px 14px;background:#f0fdf4">
        <div style="font-size:15px;font-weight:700;margin-bottom:6px">${val(data.ConsigneeName)}</div>
        ${data.ConsigneeCode ? `<div style="font-size:11px;color:#6b7280;font-family:monospace;margin-bottom:4px">${data.ConsigneeCode}</div>` : ''}
        ${data.ConsigneeContactPerson ? `<div style="font-size:12px;color:#374151;margin-bottom:2px">Contact: ${data.ConsigneeContactPerson}</div>` : ''}
        ${data.ConsigneePhone ? `<div style="font-size:12px;color:#374151;margin-bottom:2px">📞 ${data.ConsigneePhone}</div>` : ''}
        ${data.ConsigneeWhatsApp ? `<div style="font-size:12px;color:#374151">WhatsApp: ${data.ConsigneeWhatsApp}</div>` : ''}
      </div>
    </div>
  </div>

  <!-- ══ INVOICE TO ══ -->
  ${data.InvoiceToName ? `
  <div style="background:#fffbeb;border:1.5px solid #fde68a;border-radius:8px;padding:10px 16px;margin-bottom:20px;display:flex;align-items:center;gap:12px">
    <div style="font-size:10px;font-weight:700;color:#92400e;letter-spacing:0.07em;white-space:nowrap">INVOICE TO</div>
    <div style="font-size:13px;font-weight:700;color:#111">${val(data.InvoiceToName)}</div>
    ${data.InvoiceToCode ? `<div style="font-size:11px;color:#6b7280;font-family:monospace">${data.InvoiceToCode}</div>` : ''}
  </div>` : ''}

  <!-- ══ PACKAGE DETAILS ══ -->
  ${sectionHeader('📦 PACKAGE DETAILS')}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e2e8f0;border-radius:0 0 8px 8px;overflow:hidden;margin-bottom:20px">
    <table style="border-right:1px solid #e2e8f0">
      <tbody>
        ${infoRow('Description', val(data.Description))}
        ${infoRow('No. of Packages', val(data.NoOfPkgs))}
        ${infoRow('Package Type', val(data.PackageType))}
        ${infoRow('Pieces', val(data.Pieces))}
        ${infoRow('Container No. 1', data.ContainerNo, true)}
        ${infoRow('Container No. 2', data.ContainerNo2, true)}
      </tbody>
    </table>
    <table>
      <tbody>
        ${infoRow('Dimensions (L×W×H)', dimensionRow)}
        ${infoRow('Volume', data.Volume ? `${parseFloat(data.Volume).toFixed(4)} m³` : null)}
        ${infoRow('Volume Weight', data.VolumeWeight ? `${parseFloat(data.VolumeWeight).toFixed(3)} kg` : null)}
        ${infoRow('Actual Weight', data.Weight ? `${fmt(data.Weight)} kg` : null)}
        ${infoRow('Product Type', val(data.ProductType))}
        ${infoRow('Courier Type', val(data.CourierType))}
      </tbody>
    </table>
  </div>

  <!-- ══ CHARGES ══ -->
  ${sectionHeader('💰 CHARGES & PAYMENT')}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e2e8f0;border-radius:0 0 8px 8px;overflow:hidden;margin-bottom:20px">
    <table style="border-right:1px solid #e2e8f0">
      <tbody>
        ${infoRow('Transportation Charge', data.TransportationCharge ? fmt(data.TransportationCharge) : null)}
        ${infoRow('Invoice Value', data.InvoiceValue ? fmt(data.InvoiceValue) : null)}
        ${infoRow('Material Cost', data.MaterialCost && parseFloat(data.MaterialCost) > 0 ? fmt(data.MaterialCost) : null)}
        ${infoRow('HSN Code', val(data.HSNCode), true)}
      </tbody>
    </table>
    <table>
      <tbody>
        ${infoRow('Billing Type', val(data.BillingType))}
        ${infoRow('Payment Mode', val(data.PaymentMode))}
        ${infoRow('COD Amount', data.CODRequired ? fmt(data.CODAmount) : null)}
        ${infoRow('Reference / Invoice No', val(data.Reference), true)}
      </tbody>
    </table>
  </div>

  <!-- ══ TRANSPORT ══ -->
  ${sectionHeader('🚛 TRANSPORT DETAILS')}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e2e8f0;border-radius:0 0 8px 8px;overflow:hidden;margin-bottom:20px">
    <table style="border-right:1px solid #e2e8f0">
      <tbody>
        ${infoRow('Vehicle', val(data.VehicleName || data.RegistrationNo || data.VehicleID))}
        ${infoRow('Driver', val(data.DriverName || data.DriverID))}
        ${infoRow('Load Type', val(data.Load))}
      </tbody>
    </table>
    <table>
      <tbody>
        ${infoRow('Despatch Date', fmtDate(data.DespatchDate))}
        ${infoRow('Delivery Date', fmtDate(data.DeliveryDate))}
        ${infoRow('Collected By', val(data.CollectedByName || data.CollectedBy))}
        ${infoRow('Received By', val(data.ReceivedByName || data.ReceivedBy))}
      </tbody>
    </table>
  </div>

  <!-- ══ REMARKS ══ -->
  ${data.Remarks ? `
  ${sectionHeader('📝 REMARKS')}
  <div style="border:1px solid #e2e8f0;border-radius:0 0 8px 8px;padding:12px 16px;font-size:12px;color:#374151;line-height:1.7;margin-bottom:20px">
    ${data.Remarks}
  </div>` : ''}

  <!-- ══ FLAGS ══ -->
  ${(data.Transit || data.CODRequired || data.COCRequired || data.CollectMaterial) ? `
  <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px">
    ${data.Transit         ? `<span style="background:#dbeafe;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:700">IN TRANSIT</span>` : ''}
    ${data.CODRequired     ? `<span style="background:#fef9c3;color:#854d0e;border:1px solid #fde68a;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:700">COD REQUIRED</span>` : ''}
    ${data.COCRequired     ? `<span style="background:#fef9c3;color:#854d0e;border:1px solid #fde68a;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:700">COC REQUIRED</span>` : ''}
    ${data.CollectMaterial ? `<span style="background:#dcfce7;color:#166534;border:1px solid #bbf7d0;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:700">COLLECT MATERIAL</span>` : ''}
  </div>` : ''}

  <!-- ══ SIGNATURE BLOCK ══ -->
  <div style="margin-top:32px;border-top:2px solid #e2e8f0;padding-top:20px">
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px">
      ${['Consignor Signature', 'Carrier Signature', 'Consignee Signature'].map(label => `
        <div style="text-align:center">
          <div style="height:52px;border-bottom:1.5px solid #374151;margin-bottom:6px"></div>
          <div style="font-size:11px;color:#6b7280;font-weight:600">${label}</div>
        </div>`).join('')}
    </div>
    <div style="display:flex;justify-content:space-between;align-items:center;margin-top:16px">
      <div style="font-size:11px;color:#9ca3af">Generated by LTMS · ${new Date().toLocaleString('en-IN')}</div>
      <div style="font-size:11px;color:#9ca3af;font-family:monospace">${val(data.ConsignmentNo)}</div>
    </div>
  </div>

  <script>window.onload = function(){ window.print(); }</script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=960,height=760');
  if (!w) { alert('Please allow popups to print.'); return; }
  w.document.write(html);
  w.document.close();
};